import { makeAutoObservable } from 'mobx';
import { ApiError, CreateRequesterRequest, PagedRequesters, Requester, RequestersService, UpdateRequesterRequest } from 'services/featuregating/generated';

import { Loadable } from './common';

export type { PagedRequesters, Requester }

export interface IRequesterStore
{
  pagedRequesters: Loadable<PagedRequesters>,
  requester: Loadable<Requester>,

  findAsync: (query?: string, page?: number, pageSize?: number) => Promise<void>,
  requestSuggestionsAsync: (query?: string, limit?: number) => Promise<Requester[]>,
  getByTokenAsync: (token: string) => Promise<void>,
  createAsync: (name: string, path: string) => Promise<void>,
  updateAsync: (token: string, name: string, path: string) => Promise<void>,
  deleteAsync: (token: string) => Promise<void>,
}

export class RequesterStore implements IRequesterStore
{
  pagedRequesters: Loadable<PagedRequesters> = new Loadable<PagedRequesters>();
  requester: Loadable<Requester> = new Loadable<Requester>();

  constructor()
  {
    makeAutoObservable(this);
  }

  async findAsync(query: string = "", page: number = 0, pageSize: number = 30)
  {
    await this.usingLoadingAsync
    (
      this.pagedRequesters,
      async () =>
      {
        const pagedRequesters = await RequestersService.getRequesters({query: query, page: `${page}`, pageSize: `${pageSize}`});
        this.setPagedRequesters(pagedRequesters);
      },
    );
  }

  async requestSuggestionsAsync(query: string = "", limit: number = 30)
  {
    try
    {
      const pagedRequesters = await RequestersService.getRequesters({query: query, page: '0', pageSize: `${limit}`});
      return pagedRequesters.data || [];
    }
    catch (e)
    {
      return [];
    }
  }

  async getByTokenAsync(token: string)
  {
    await this.usingLoadingAsync
    (
      this.requester,
      async () =>
      {
        const requester = await RequestersService.getRequesterByToken({token: token});
        this.setRequester(requester);
      },
      (error: Error) =>
      {
        const apiError = error as ApiError;
        if (apiError && apiError.status === 404)
        {
          this.setRequester();
        }
        else
        {
          this.requester.setError(error);
        }
      },
    );
  }

  async updateAsync(token: string, name: string, path: string)
  {
    const request: UpdateRequesterRequest =
    {
      name: name,
      path: path,
    };
    await this.usingLoadingAsync
    (
      this.requester,
      async () =>
      {
        const requester = await RequestersService.patchRequesterByToken({token: token, requestBody: request});
        this.setRequester(requester);
        await this.pagedRequesters.refreshAsync();
      },
    );
  }

  async createAsync(name: string, path: string)
  {
    const request: CreateRequesterRequest =
    {
      name: name,
      path: path,
    };
    await this.usingLoadingAsync
    (
      this.requester,
      async () =>
      {
        const requester = await RequestersService.postRequesters({requestBody: request});
        console.log(requester)
        this.setRequester(requester);
        await this.pagedRequesters.refreshAsync();
      },
    );
  }

  async deleteAsync(token: string)
  {
    await this.usingLoadingAsync
    (
      this.requester,
      async () =>
      {
        await RequestersService.deleteRequesterByToken({token: token});
        this.setRequester();
        await this.pagedRequesters.refreshAsync();
      },
    );
  }

  private async usingLoadingAsync<T>(loadable: Loadable<T>, actionAsync: () => Promise<void>, errorHandler?: <T extends Error>(e: T) => void)
  {
    loadable.clearError();
    loadable.setIsLoading(true);
    try
    {
      await actionAsync();
      loadable.dataRefreshOperator = async () => await actionAsync();
    }
    catch (e)
    {
      const error = e as Error;
      if (errorHandler)
      {
        errorHandler(error);
      }
      else
      {
        loadable.setError(error);
      }
    }
    finally
    {
      loadable.setIsLoading(false);
    }
  }
  private setPagedRequesters(pagedRequesters?: PagedRequesters): void
  {
    this.pagedRequesters.data = pagedRequesters;
  }
  private setRequester(requester?: Requester): void
  {
    this.requester.data = requester;
  }
}